/**
 * Routing on the client: read the current route and build URLs that keep
 * the language prefix and the context params (open project, ...) — the JS
 * mirror of the server's url() (framework docs: routing.md).
 *
 * Works off `settings.routing`, inlined by the server at render time
 * (g.init): the page's language + the site's language setup, the declared
 * context params WITH the values they had when the page rendered (so every
 * tab keeps its own open project), and the matched route. The client never
 * sees route settings and builds from patterns, not route names.
 */
import { settings } from './core.js';

const cfg = () => settings.routing || {};

// '/p/{project:int}/tasks' -> segment list; constraints are server business,
// here they only shape parsing ({x:int} values come back as numbers)
function parsePattern(pattern) {
	return String(pattern).split('/').filter(Boolean).map((segment) => {
		const m = segment.match(/^\{([A-Za-z_][A-Za-z0-9_]*)(\.\.\.|:([^}]+))?\}$/);
		if (!m) return { lit: segment };
		return { param: m[1], catchAll: m[2] === '...', int: m[3] === 'int' };
	});
}

// The language code a path starts with, when the site keeps it in the URL.
function pathLang(path) {
	const { languageInUrl, languages = [] } = cfg();
	if (!languageInUrl) return null;
	const first = path.split('/').filter(Boolean)[0];
	return languages.includes(first) ? first : null;
}

export const route = {
	/** The matched route of this page: { name, pattern, params, path, lang }. */
	current() {
		const { route: current = {}, lang = null } = cfg();
		return { name: null, pattern: null, params: {}, path: null, ...current, lang };
	},

	/** The language this page renders in (null when the server sent none). */
	lang() {
		return cfg().lang ?? null;
	},

	/** Context param value(s): g.route.context('project') or the whole object. */
	context(name) {
		const values = cfg().context || {};
		return name === undefined ? { ...values } : (values[name] ?? null);
	},

	/**
	 * Read a URL (absolute or path): strip the language prefix, strip the
	 * context prefix (/p/42-style segments -> `context`), parse the query —
	 * and, given a pattern, extract its params:
	 *   g.route.parse('/ro/p/42/reports/7', '/reports/{id:int}')
	 *   -> { lang: 'ro', context: { project: 42 }, path: '/reports/7',
	 *        query: {}, params: { id: 7 } }
	 * `params` is null when the pattern is missing or does not match.
	 */
	parse(url, pattern) {
		const u = new URL(url, window.location.origin);
		const query = Object.fromEntries(u.searchParams);
		let path = u.pathname.replace(/\/{2,}/g, '/').replace(/(.)\/$/, '$1');
		const lang = pathLang(path);
		if (lang) path = '/' + path.split('/').filter(Boolean).slice(1).join('/');

		// consume leading marker/value context pairs
		const markers = cfg().contextMarkers || {};
		const byMarker = Object.fromEntries(Object.entries(markers).map(([param, marker]) => [marker, param]));
		const context = {};
		const parts = path.split('/').filter(Boolean);
		while (parts.length >= 2 && byMarker[parts[0]] !== undefined && !(byMarker[parts[0]] in context)) {
			const param = byMarker[parts[0]];
			const value = parts[1];
			// tenant params carry LIDS — always strings, even an all-digit one
			// (mirrors the server's stripContext)
			context[param] = param !== 'company' && param !== 'space' && /^\d+$/.test(value) ? Number(value) : value;
			parts.splice(0, 2);
		}
		path = '/' + parts.join('/');

		let params = null;
		if (pattern !== undefined) {
			params = {};
			const segments = parsePattern(pattern);
			const parts = path.split('/').filter(Boolean);
			for (let i = 0; i < segments.length; i++) {
				const s = segments[i];
				if (s.catchAll) {
					if (parts.length <= i) { params = null; break; }
					params[s.param] = parts.slice(i).join('/');
					parts.length = i; // consumed
					break;
				}
				const part = parts[i];
				if (part === undefined || (s.lit !== undefined && s.lit !== part)) { params = null; break; }
				if (s.param) params[s.param] = s.int && /^\d+$/.test(part) ? Number(part) : part;
			}
			if (params && !segments.some((s) => s.catchAll) && path.split('/').filter(Boolean).length !== segments.length) params = null;
		}

		return { lang, context, path, query, params };
	},

	/**
	 * Build a URL from a pattern — the JS twin of the server's url(). The
	 * page's context prefix and language are carried over automatically:
	 * on /ro/p/42/tasks, g.route.url('/pricing') is '/ro/p/42/pricing'.
	 *   g.route.url('/reports/{id}', { id: 7, tab: 'x' }) // '/p/42/reports/7?tab=x'
	 *   g.route.url('/pricing', {}, { context: false })   // no context prefix
	 *   g.route.url('/pricing', {}, { context: { project: false } }) // drop one
	 *   g.route.url('/pricing', {}, { context: { project: 99 } })    // force one
	 *   g.route.url('/pricing', {}, { lang: 'de' })       // another language
	 * Pattern params fill explicit > context > Error; leftovers become the
	 * query string.
	 */
	url(pattern, params = {}, options = {}) {
		const { languageInUrl, defaultLang, context = {}, contextMarkers = {} } = cfg();
		const leftover = { ...params };
		const consumed = new Set();

		const parts = parsePattern(pattern).map((s) => {
			if (s.lit !== undefined) return s.lit;
			consumed.add(s.param);
			let value = leftover[s.param] ?? context[s.param];
			delete leftover[s.param];
			if (value === undefined || value === null) throw new Error(`g.route.url: '${s.param}' of '${pattern}' not passed and not in the context`);
			value = String(value);
			return s.catchAll ? value.split('/').map(encodeURIComponent).join('/') : encodeURIComponent(value);
		});
		let path = '/' + parts.join('/');

		// context prefix: option override > the page's context
		const ctxOpt = options.context ?? {};
		if (ctxOpt !== false) {
			let prefix = '';
			for (const [param, marker] of Object.entries(contextMarkers)) {
				if (consumed.has(param)) continue;
				const value = param in ctxOpt ? ctxOpt[param] : context[param];
				if (value === false || value === undefined || value === null) continue;
				prefix += `/${marker}/${encodeURIComponent(value)}`;
			}
			path = prefix + (path === '/' ? '' : path) || '/';
		}

		if (languageInUrl) {
			const lang = options.lang !== undefined ? options.lang : cfg().lang;
			if (lang && lang !== defaultLang) path = `/${lang}` + (path === '/' ? '' : path);
		}

		const query = new URLSearchParams({ ...leftover, ...(options.query || {}) }).toString();
		return (options.absolute ? window.location.origin : '') + path + (query ? '?' + query : '');
	},
};
